import { Controller } from "@hotwired/stimulus";

import logger from "~/lib/logger";

const log = logger();

type CeremonyMode = "create" | "get" | "conditional";

// NOTE: From 2027, replace decode() and encode() with the base64 methods on
// Uint8Array, which had about 87% browser support in 2026:
//   Uint8Array.fromBase64(value, { alphabet: "base64url" }).buffer
//   new Uint8Array(value).toBase64({ alphabet: "base64url", omitPadding: true })
function decode(value: string): ArrayBuffer {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, "="));
  return Uint8Array.from(binary, (character) => character.charCodeAt(0)).buffer;
}

function encode(value: ArrayBuffer): string {
  const binary = String.fromCharCode(...new Uint8Array(value));
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function withBuffers(
  options: Record<string, unknown>,
): PublicKeyCredentialCreationOptions & PublicKeyCredentialRequestOptions {
  const publicKey = { ...(options.publicKey as Record<string, unknown>) };
  publicKey.challenge = decode(publicKey.challenge as string);
  const user = publicKey.user as Record<string, unknown> | undefined;
  if (user) {
    publicKey.user = { ...user, id: decode(user.id as string) };
  }
  for (const key of ["allowCredentials", "excludeCredentials"]) {
    const list = publicKey[key] as Array<Record<string, unknown>> | undefined;
    if (list) {
      publicKey[key] = list.map((entry) => ({
        ...entry,
        id: decode(entry.id as string),
      }));
    }
  }
  // The server built this structure; only its binary fields needed decoding.
  return publicKey as unknown as PublicKeyCredentialCreationOptions &
    PublicKeyCredentialRequestOptions;
}

function serialize(credential: PublicKeyCredential): Record<string, unknown> {
  const response = credential.response;
  const serialized: Record<string, unknown> = {
    id: credential.id,
    rawId: encode(credential.rawId),
    type: credential.type,
  };
  if (response instanceof AuthenticatorAttestationResponse) {
    serialized.response = {
      clientDataJSON: encode(response.clientDataJSON),
      attestationObject: encode(response.attestationObject),
      transports: response.getTransports?.() ?? [],
    };
  } else if (response instanceof AuthenticatorAssertionResponse) {
    serialized.response = {
      clientDataJSON: encode(response.clientDataJSON),
      authenticatorData: encode(response.authenticatorData),
      signature: encode(response.signature),
      userHandle: response.userHandle ? encode(response.userHandle) : null,
    };
  }
  return serialized;
}

export default class PasskeyController extends Controller<HTMLElement> {
  static override values = {
    options: Object,
    optionsUrl: String,
    mode: String,
    autostart: Boolean,
  } as const;

  static override targets = ["error"];

  declare readonly optionsValue: Record<string, unknown>;
  declare readonly optionsUrlValue: string;
  declare readonly hasOptionsUrlValue: boolean;
  declare readonly modeValue: CeremonyMode;
  declare readonly autostartValue: boolean;
  declare readonly errorTarget: HTMLElement;
  declare readonly hasErrorTarget: boolean;

  private form: HTMLFormElement | null = null;
  private abort: AbortController | null = null;
  // A re-rendered form still carries the spent assertion in its hidden field,
  // so only a ceremony this controller ran may submit.
  private signed = false;
  private autostartTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly onSubmit = (event: Event): void => {
    if (this.signed) {
      return;
    }
    event.preventDefault();
    void this.run();
  };
  // htmx handles the same submit event and does not check preventDefault. It
  // would post the empty field and replace the form before the ceremony ends,
  // which detaches the form that the submit below needs.
  private readonly onBeforeRequest = (event: Event): void => {
    if (!this.signed) {
      event.preventDefault();
    }
  };

  override connect(): void {
    this.form = this.element.closest("form");
    this.form?.addEventListener("submit", this.onSubmit);
    this.form?.addEventListener("htmx:before:request", this.onBeforeRequest);
    if (this.modeValue === "conditional") {
      void this.runConditional();
    } else if (this.autostartValue) {
      // A password manager can lose a ceremony that starts while the page
      // loads, even after the load event.
      this.autostartTimer = setTimeout(() => void this.run(), 100);
    }
  }

  override disconnect(): void {
    if (this.autostartTimer !== null) {
      clearTimeout(this.autostartTimer);
    }
    this.abort?.abort();
    this.form?.removeEventListener("submit", this.onSubmit);
    this.form?.removeEventListener("htmx:before:request", this.onBeforeRequest);
  }

  // The browser shows the passkey beside the addresses it fills in, so the
  // ceremony has to be waiting before the user reaches the email field.
  private async runConditional(): Promise<void> {
    if (!(await PublicKeyCredential.isConditionalMediationAvailable?.())) {
      return;
    }
    await this.run("conditional");
  }

  private get input(): HTMLInputElement | null {
    return this.form?.querySelector('input[name="credential"]') ?? null;
  }

  private async run(
    mediation: CredentialMediationRequirement = "optional",
  ): Promise<void> {
    const input = this.input;
    if (!input) {
      return;
    }
    // A password manager can hold a ceremony without a prompt and never settle
    // it, so a submit replaces the ceremony in progress.
    this.abort?.abort();
    const abort = new AbortController();
    this.abort = abort;
    this.showError(null);
    try {
      const options = await this.options();
      if (abort.signal.aborted) {
        return;
      }
      if (!options) {
        this.showError("Signing in with a passkey is unavailable right now.");
        return;
      }
      const publicKey = withBuffers(options);
      const credential = (await (this.modeValue === "create"
        ? navigator.credentials.create({ publicKey, signal: abort.signal })
        : navigator.credentials.get({
            publicKey,
            mediation,
            signal: abort.signal,
          }))) as PublicKeyCredential | null;
      if (!credential) {
        this.showError("No passkey was returned. Please try again.");
        return;
      }
      input.value = JSON.stringify(serialize(credential));
      this.signed = true;
      this.form?.requestSubmit();
    } catch (error) {
      if (abort.signal.aborted) {
        return;
      }
      log.warn("Passkey ceremony failed", { error });
      this.showError(
        this.modeValue === "create"
          ? "This device could not create a passkey. Please try again."
          : "No passkey was available on this device.",
      );
    }
  }

  private async options(): Promise<Record<string, unknown> | null> {
    if (!this.hasOptionsUrlValue) {
      return this.optionsValue;
    }
    const response = await fetch(this.optionsUrlValue, { method: "POST" });
    if (!response.ok) {
      return null;
    }
    return (await response.json()) as Record<string, unknown>;
  }

  private showError(message: string | null): void {
    // A mediated ceremony has no error target: nobody asked for it, so a
    // failure needs no notice.
    if (!this.hasErrorTarget) {
      return;
    }
    this.errorTarget.textContent = message ?? "";
    this.errorTarget.classList.toggle("hidden", message === null);
  }
}
